import React, { useState, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Alert,
  Avatar,
  Autocomplete,
  Card,
  CardContent,
  CardActions,
  FormGroup,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import Add from '@mui/icons-material/Add';
import Edit from '@mui/icons-material/Edit';
import Delete from '@mui/icons-material/Delete';
import Search from '@mui/icons-material/Search';
import Phone from '@mui/icons-material/Phone';
import Email from '@mui/icons-material/Email';
import Storefront from '@mui/icons-material/Storefront';
import ConfirmationNumber from '@mui/icons-material/ConfirmationNumber';
import { Worker, CrudPermissions, WorkerType } from '../../types';
import { mockWorkers } from '../../mock/workers';
import { mockFees } from '../../mock/fees';
import { v4 as uuidv4 } from 'uuid';

interface WorkersManagementProps {
  permissions: CrudPermissions;
}

export const WorkersManagement: React.FC<WorkersManagementProps> = ({ permissions }) => {
  const [workers, setWorkers] = useState<Worker[]>(mockWorkers);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingWorker, setEditingWorker] = useState<Worker | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [formData, setFormData] = useState<Partial<Worker>>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    workerTypes: ['concession'],
    assignedEventIds: [],
    isActive: true,
  });

  const availableEvents = useMemo(() => {
    return mockFees.filter(f => f.isActive && f.feeType === 'event_ticket');
  }, []);

  const filteredWorkers = useMemo(() => {
    return workers.filter((worker) => {
      const matchesSearch =
        worker.firstName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        worker.lastName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        worker.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
        worker.phone.includes(searchQuery);
      const matchesType = typeFilter === 'all' || worker.workerTypes.includes(typeFilter as WorkerType);
      return matchesSearch && matchesType;
    });
  }, [workers, searchQuery, typeFilter]);

  const handleAdd = () => {
    setEditingWorker(null);
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      workerTypes: ['concession'],
      assignedEventIds: [],
      isActive: true,
    });
    setOpenDialog(true);
  };

  const handleEdit = (worker: Worker) => {
    setEditingWorker(worker);
    setFormData({
      ...worker,
    });
    setOpenDialog(true);
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to delete this worker?')) {
      setWorkers(workers.filter((w) => w.id !== id));
      const index = mockWorkers.findIndex((w) => w.id === id);
      if (index !== -1) {
        mockWorkers.splice(index, 1);
      }
    }
  };

  const handleSave = () => {
    if (editingWorker) {
      const updatedWorker = { ...editingWorker, ...formData } as Worker;
      setWorkers(workers.map((w) => (w.id === editingWorker.id ? updatedWorker : w)));
      const index = mockWorkers.findIndex((w) => w.id === editingWorker.id);
      if (index !== -1) {
        mockWorkers[index] = updatedWorker;
      }
    } else {
      const newWorker: Worker = {
        id: uuidv4(),
        firstName: formData.firstName || '',
        lastName: formData.lastName || '',
        email: formData.email || '',
        phone: formData.phone || '',
        workerTypes: formData.workerTypes || ['concession'],
        assignedEventIds: formData.assignedEventIds,
        isActive: formData.isActive ?? true,
        createdAt: new Date(),
      };
      setWorkers([...workers, newWorker]);
      mockWorkers.push(newWorker);
    }
    setOpenDialog(false);
  };

  const formatPhoneNumber = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 6) return `(${numbers.slice(0, 3)}) ${numbers.slice(3)}`;
    return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
  };

  const handleWorkerTypeToggle = (type: WorkerType) => {
    const currentTypes = formData.workerTypes || [];
    if (currentTypes.includes(type)) {
      if (currentTypes.length > 1) {
        const newTypes = currentTypes.filter(t => t !== type);
        setFormData({
          ...formData,
          workerTypes: newTypes,
          assignedEventIds: newTypes.includes('ticket') ? formData.assignedEventIds : [],
        });
      }
    } else {
      setFormData({
        ...formData,
        workerTypes: [...currentTypes, type],
      });
    }
  };

  const getWorkerIcon = (workerTypes: WorkerType[]) => {
    if (workerTypes.includes('concession') && workerTypes.includes('ticket')) {
      return <Storefront />;
    }
    if (workerTypes.includes('ticket')) {
      return <ConfirmationNumber />;
    }
    return <Storefront />;
  };

  const getWorkerIconColor = (workerTypes: WorkerType[]) => {
    if (workerTypes.includes('concession') && workerTypes.includes('ticket')) {
      return '#059669';
    }
    if (workerTypes.includes('ticket')) {
      return '#3b82f6';
    }
    return '#f59e0b';
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between',
        alignItems: { xs: 'stretch', sm: 'center' },
        gap: 2,
        mb: 3,
      }}>
        <Typography variant="h5" fontWeight={700}>
          Workers Management
        </Typography>
        {permissions.create && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleAdd}
            sx={{
              background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
              },
              flexShrink: 0,
            }}
          >
            Add Worker
          </Button>
        )}
      </Box>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Search workers..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            sx={{ flex: 1, minWidth: 200 }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Worker Type</InputLabel>
            <Select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              label="Worker Type"
            >
              <MenuItem value="all">All Types</MenuItem>
              <MenuItem value="concession">Concession</MenuItem>
              <MenuItem value="ticket">Ticket</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      <Box sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          md: 'repeat(2, 1fr)',
          lg: 'repeat(3, 1fr)',
        },
        gap: 3,
      }}>
        {filteredWorkers.map((worker) => (
          <Card
            key={worker.id}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              borderRadius: 2,
              border: '1px solid',
              borderColor: worker.isActive ? '#e5e7eb' : '#fecaca',
              opacity: worker.isActive ? 1 : 0.7,
            }}
          >
            <CardContent sx={{ flexGrow: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0, flex: 1 }}>
                  <Avatar
                    sx={{
                      bgcolor: getWorkerIconColor(worker.workerTypes),
                      width: 48,
                      height: 48,
                      flexShrink: 0,
                    }}
                  >
                    {getWorkerIcon(worker.workerTypes)}
                  </Avatar>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                      variant="subtitle1"
                      fontWeight={600}
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {worker.firstName} {worker.lastName}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                      {worker.workerTypes.map((type) => (
                        <Chip
                          key={type}
                          label={type === 'concession' ? 'Concession' : 'Ticket'}
                          size="small"
                          sx={{
                            bgcolor: type === 'concession' ? '#fef3c7' : '#dbeafe',
                            color: type === 'concession' ? '#92400e' : '#1e40af',
                            height: 20,
                            fontSize: '0.7rem',
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                </Box>
                {!worker.isActive && (
                  <Chip label="Inactive" size="small" color="error" variant="outlined" sx={{ flexShrink: 0 }} />
                )}
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                  <Phone fontSize="small" color="action" sx={{ flexShrink: 0 }} />
                  <Typography variant="body2" noWrap>{worker.phone}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                  <Email fontSize="small" color="action" sx={{ flexShrink: 0 }} />
                  <Typography variant="body2" noWrap>{worker.email}</Typography>
                </Box>
              </Box>

              {worker.workerTypes.includes('ticket') && worker.assignedEventIds && worker.assignedEventIds.length > 0 && (
                <Box sx={{ mt: 2, p: 1.5, bgcolor: '#f8fafc', borderRadius: 1 }}>
                  <Typography variant="caption" fontWeight={600} color="text.secondary">
                    Assigned Events ({worker.assignedEventIds.length})
                  </Typography>
                </Box>
              )}
            </CardContent>

            {(permissions.update || permissions.delete) && (
              <CardActions sx={{ px: 2, pb: 2, pt: 0 }}>
                {permissions.update && (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Edit />}
                    onClick={() => handleEdit(worker)}
                    sx={{ flexGrow: 1, minWidth: 0 }}
                  >
                    Edit
                  </Button>
                )}
                {permissions.delete && (
                  <IconButton
                    size="small"
                    onClick={() => handleDelete(worker.id)}
                    color="error"
                    sx={{ flexShrink: 0 }}
                  >
                    <Delete />
                  </IconButton>
                )}
              </CardActions>
            )}
          </Card>
        ))}
      </Box>

      {filteredWorkers.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary">
            No workers found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {searchQuery || typeFilter !== 'all'
              ? 'Try adjusting your search or filter'
              : 'Click "Add Worker" to create your first worker'}
          </Typography>
        </Box>
      )}

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingWorker ? 'Edit Worker' : 'Add New Worker'}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <TextField
                label="First Name"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                fullWidth
              />
              <TextField
                label="Last Name"
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                fullWidth
              />
            </Box>

            <TextField
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              fullWidth
            />

            <TextField
              label="Phone Number"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: formatPhoneNumber(e.target.value) })}
              placeholder="(555) 123-4567"
              fullWidth
            />

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Worker Roles
              </Typography>
              <FormGroup row>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.workerTypes?.includes('concession') || false}
                      onChange={() => handleWorkerTypeToggle('concession')}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Storefront fontSize="small" sx={{ color: '#f59e0b' }} />
                      <span>Concession Worker</span>
                    </Box>
                  }
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.workerTypes?.includes('ticket') || false}
                      onChange={() => handleWorkerTypeToggle('ticket')}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ConfirmationNumber fontSize="small" sx={{ color: '#3b82f6' }} />
                      <span>Ticket Worker</span>
                    </Box>
                  }
                />
              </FormGroup>
            </Box>

            {formData.workerTypes?.includes('ticket') && (
              <Autocomplete
                multiple
                options={availableEvents}
                getOptionLabel={(option) => option.name}
                value={availableEvents.filter(e => formData.assignedEventIds?.includes(e.id))}
                onChange={(_, newValue) => {
                  setFormData({ ...formData, assignedEventIds: newValue.map(e => e.id) });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Assigned Events"
                    placeholder="Select events..."
                  />
                )}
              />
            )}

            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.isActive ? 'active' : 'inactive'}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.value === 'active' })}
                label="Status"
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>

            <Alert severity="info" sx={{ mt: 1 }}>
              Workers sign in using their phone number and a verification code sent via SMS.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!formData.firstName || !formData.lastName || !formData.email || !formData.phone || !formData.workerTypes?.length}
            sx={{
              background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
              },
            }}
          >
            {editingWorker ? 'Save Changes' : 'Add Worker'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
